import { RequestValue } from "@/api/options";
import { PersonalDataFetch } from "@/api/personalData";
import { sendStepUserJourney, sendUserJourney } from "@/api/userJourney";
import { payments } from "@/app/personal-data/contants";

import useBreakpoint from "@/hooks/useBreakpoint";
import useEmailAutocomplete from "@/hooks/useEmailAutocomplete";
import usePageSubmit from "@/hooks/usePageSubmit";
import useTabsValidation from "@/hooks/useTabsValidation";
import { useTagManager } from "@/hooks/useTagManager";
import { useFormStore } from "@/store/form";
import { usePhoneBlockSaleStore } from "@/store/phoneBlock";
import { useProductStore } from "@/store/product";
import { useProvisionalLineSaleStore } from "@/store/provisionalLineSale";
import { getOnlyNumbers } from "@/utils";
import { formatPrice } from "@/utils/currency";
import {
  getMarketingInfo,
  getUuidStorageSession,
  setUuidStorageSession,
} from "@/utils/saleSessionStorage";
import {
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { GenericObject } from "@/utils/types";
import { useCallback, useEffect, useState } from "react";
import { v4 as uuidV4 } from "uuid";
import * as yup from "yup";

const personalDataSchema = yup.object().shape({
  email: yup
    .string()
    .email("O e-mail digitado não é válido")
    .required("O e-mail é obrigatório"),
  tel: yup.string().tel().required("O telefone é obrigatório"),
  cpf: yup.string().cpf().required("O CPF é obrigatório"),
  autorizarContato: yup
    .bool()
    .oneOf([true], "Você deve aceitar para continuar"),
});

interface PageValidationSchema {
  tel: yup.ObjectSchema<{ tel: string }>;
  cpf: yup.ObjectSchema<{ cpf: string }>;
  [key: string]: yup.AnyObjectSchema;
}

const pageValidationSchema: PageValidationSchema = {
  tel: yup.object().shape({
    tel: yup.string().tel().required("O telefone é obrigatório"),
  }),
  cpf: yup.object().shape({
    cpf: yup.string().cpf().required("O CPF é obrigatório"),
  }),
};

type PageConfig = {
  route: string;
  prevRoute: string;
  nextRoute: string;
};

const defaultPageConfig = {
  route: "/personal-data",
  prevRoute: "/",
  nextRoute: "/type-product",
};

export function usePersonalData() {
  const { dataLayer, dataLayerOmega } = useTagManager();
  const { isOnBreakpoint: isTableOrLess } = useBreakpoint(1024);
  const [pageConfig, setPageConfig] = useState<PageConfig>(defaultPageConfig);

  useTabsValidation(pageConfig.route, pageConfig.prevRoute);

  const form = useFormStore((store) => store.value);

  const shouldAddSpecialBonus = useProductStore(
    (store) => store.shouldAddSpecialBonus
  );
  const setPayment = useProductStore((store) => store.setPayment);
  const isExpress = useProductStore((store) => store.isExpress);
  const saleId = useFormStore((store) => store.saleId);
  const [typeProductSelected] = useState(form.tipo || "MIGRACAO");

  const [cpf, setCpf] = useState(form.cpf);
  const [email, setEmail] = useState(form.email);
  const [tel, setTel] = useState(form.tel);
  const [autorizarContato, setAutorizarContato] = useState(
    form.autorizarContato
  );
  const [formIsValid, setFormaIsValid] = useState(false);

  const { data, isLoading } = PersonalDataFetch(cpf);
  const addData = useFormStore((store) => store.addData);
  const product = useProductStore((store) => store.item);
  const addBonusPortability = useProductStore(
    (store) => store.addBonusPortability
  );
  const removeBonusPortability = useProductStore(
    (store) => store.removeBonusPortability
  );
  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(form.tel).slice(0, 2))
  );
  const loyalty = useProductStore((store) => store.loyalty);
  const totalGbProduct = useProductStore((store) => store.getTotalGbProduct());
  const provisionalLine = useProvisionalLineSaleStore(
    (store) => store.provisionalLine
  );
  const isPos = useProductStore((store) => store.isPos);
  const phoneBlock = usePhoneBlockSaleStore((store) => store.phoneBlock);

  const [oldPrice, setOldPrice] = useState(0);
  const [showChangePriceAlert, setShowChangePriceAlert] = useState(false);
  const [sendDataLayer, setSendDataLayer] = useState(false);

  const { bind, isValid: IsEmailValid } = useEmailAutocomplete({
    defaultValue: form.email,
  });
  const isMultProduct = import.meta.env.VITE_CHOOSE_PRODUCT_TYPE === "1";

  const handleUserJourney = useCallback(
    (name: string, value: unknown) => {
      addData({
        [name]: value,
      });
      sendUserJourney({
        ...form,
        product: product!,
        loyalty,
        ...{
          [name]: value,
          passo: "DADOS_PESSOAIS",
        },
      });
    },
    [addData, form, product, loyalty]
  );

  const handleOnBlurUserJourney = useCallback(
    (name: string, value: unknown) => {
      pageValidationSchema[name]
        .isValid({
          [name]: value,
        })
        .then((isValid: boolean) => {
          if (isValid) {
            handleUserJourney(name, value);
          }
        });
    },
    [handleUserJourney]
  );

  const onClickForwardButton = (nextRoute: string) => {
    let passo = "ENDERECO";

    if (nextRoute === "/personal-data/complement") {
      passo = "DADOS_PESSOAIS_COMPLEMENTO";
    } else if (nextRoute === "/personal-data/type-product") {
      passo = "ESCOLHER_TIPO";
    }

    sendStepUserJourney({
      passo,
      uuid: form.uuid,
    });
  };

  const handlerGBProduct = () => {
    if (isMultProduct && !isTableOrLess) {
      if (form.tipo === "PORTABILIDADE" || shouldAddSpecialBonus()) {
        addBonusPortability();
      } else {
        removeBonusPortability();
      }
    }
  };

  const submitCallback = usePageSubmit(pageConfig.nextRoute);
  const handleOnSubmit = (formData: GenericObject) => {
    dataLayer({
      event: "step",
      passo: "DADOS-PESSOAIS",
      sku: removeAccentsAndSetUpperCase(product?.tim_id),
      nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
        product?.nome
      )}-${totalGbProduct}`,
      valor: formatPrice(currentPrice),
      status: "Sucesso",
    });

    handlerGBProduct();
    submitCallback({
      ...formData,
      tipo: typeProductSelected,
      possuiProvisorio: provisionalLine === 0 ? 0 : 1,
    });
  };

  useEffect(() => {
    const uuidForm = form.uuid;
    if (uuidForm) {
      setUuidStorageSession(uuidForm);
    }

    if (!form.uuid) {
      const uuidStorageSession = getUuidStorageSession();
      if (uuidStorageSession) {
        addData({ uuid: JSON.parse(uuidStorageSession) });
      } else {
        const uuid = uuidV4();
        setUuidStorageSession(uuid);
        addData({ uuid });
      }
    }
  }, [addData, form]);

  useEffect(() => {
    addData({ tel });
  }, [tel, addData]);

  useEffect(() => {
    const isValidShowPriceAlert = oldPrice && currentPrice !== oldPrice;

    if (isValidShowPriceAlert) {
      setShowChangePriceAlert(true);
    }

    setOldPrice(currentPrice);
  }, [currentPrice, oldPrice]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const { found } = data;
    addData({ found });

    const nextRoute = found ? "/address-info" : "/personal-data/complement";

    setPageConfig((oldConfig) => ({ ...oldConfig, nextRoute }));
  }, [data, addData, isTableOrLess, isMultProduct]);

  useEffect(() => {
    const marketingInfo = getMarketingInfo();
    if (marketingInfo) {
      addData(JSON.parse(marketingInfo));
    }

    const uuidStorageSession = getUuidStorageSession();
    if (uuidStorageSession) {
      addData(JSON.parse(uuidStorageSession));
    }

    let payment = payments.fatura;

    if (isExpress()) {
      payment = payments.cartaodecredito;
    }

    setPayment(payment as RequestValue);
  }, [addData, isExpress, product, setPayment]);

  useEffect(() => {
    personalDataSchema
      .isValid({
        cpf,
        email,
        tel,
        autorizarContato,
      })
      .then((isValid) => {
        setFormaIsValid(isValid);
      })
      .catch(() => {
        setFormaIsValid(false);
      });
  }, [cpf, email, tel, autorizarContato]);

  useEffect(() => {
    if (sendDataLayer) return;

    dataLayerOmega({
      event: "begin_checkout",
      ecommerce: {
        transaction_id: saleId,
        currency: "BRL",
        items: [
          {
            item_name: removeAccentAndSpaceAndSetLowerCase(product?.nome),
            item_id: product?.tim_id,
            price: formatPrice(currentPrice),
            item_variant: isPos() ? "Plano TIM Black" : "Plano TIM Controle",
            quantity: 1,
          },
        ],
      },
    });
    setSendDataLayer(true);
  }, [currentPrice, dataLayerOmega, isPos, product, saleId, sendDataLayer]);

  return {
    showChangePriceAlert,
    currentPrice,
    personalDataSchema,
    saleId,
    email,
    bind,
    IsEmailValid,
    phoneBlock,
    autorizarContato,
    cpf,
    formIsValid,
    isLoading,
    pageConfig,
    tel,
    setShowChangePriceAlert,
    handleOnSubmit,
    setEmail,
    handleUserJourney,
    setTel,
    setAutorizarContato,
    setCpf,
    handleOnBlurUserJourney,
    onClickForwardButton,
    data,
    addData,
    setPageConfig,
    isMultProduct,
    isTableOrLess,
  };
}
